'use client'

import React from 'react'
import classNames from 'classnames'
import { Title, type HeadingTag } from '@/ui/components/Title'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import { Input } from '@/ui/atoms/Input'
import { useNewsletterSubscribe } from './useNewsletterSubscribe'
import { HoneypotField } from './HoneypotField'

interface NewsletterSignupProps {
  audienceType?: string
  title?: string
  titleHierarchy?: HeadingTag
  subtitle?: string
  placeholder?: string
  buttonText?: string
  className?: string
}

export const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  audienceType = 'general',
  title = 'Stay Updated',
  titleHierarchy = 'h2',
  subtitle = 'Get the latest insights on electrification technology, industry trends, and platform updates delivered to your inbox.',
  placeholder = 'Enter your email address',
  buttonText = 'Subscribe',
  className,
}) => {
  const { email, setEmail, honeypot, setHoneypot, isSubmitting, status, handleSubmit } =
    useNewsletterSubscribe(audienceType)

  return (
    <div id="newsletter" className={classNames('w-full', className)}>
      <div className="bg-bg-subtle rounded-md p-6 lg:p-8">
        <div className="max-w-2xl mx-auto text-center">
          {/* Header */}
          <Title
            className="!text-headline-M-semibold text-text-base mb-3"
            hierarchy={titleHierarchy}
          >
            {title}
          </Title>
          {subtitle && (
            <p className="text-body-M-regular text-text-subtle mb-6">
              {subtitle}
            </p>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              placeholder={placeholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              className="flex-1 mt-0"
            />
            <HoneypotField value={honeypot} onChange={setHoneypot} />
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={isSubmitting || !email}
              className="sm:w-auto"
            >
              <ButtonContent label={isSubmitting ? 'Subscribing...' : buttonText} />
            </Button>
          </form>

          {/* Success Message */}
          {status === 'success' && (
            <div className="mt-4 text-body-S-regular text-green-40">
              You&rsquo;re in &mdash; you&rsquo;ll get a confirmation email within the next 10 minutes.
            </div>
          )}

          {/* Error Message */}
          {status === 'error' && (
            <div className="mt-4 text-body-S-regular text-red-60">
              Something went wrong. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
